import { Link } from "react-router-dom";
import { useGetScans } from "../../../../helpers/queries/scans/useGetScans";
import { Spinner } from "../../../../components/Spinner";
import { useEffect, useRef, useState } from "react";
import { exportAsCsv, exportAsPdf } from "../../../../helpers/api";
import { Card } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { cn, formatFullDate, formatTime } from "../../../../lib/utils";
import { FaCircleCheck } from "react-icons/fa6";
import { MdPending } from "react-icons/md";
import { IoIosCloseCircle } from "react-icons/io";
import { FiPieChart, FiCompass } from "react-icons/fi";
import { LuDownload } from "react-icons/lu";
import empty from "../../../../assets/images/empty_scan.png";
import RunningStatus from "../../../../components/RunningStatus";

const RecentScans = () => {
  const [page] = useState(1);

  const { isLoading, isSuccess, isError, data } = useGetScans(page);

  const [activeModalId, setActiveModalId] = useState(null);

  const [showExtra, setShowExtra] = useState(null);

  const modalRef = useRef(null);

  const showModal = (id) => {
    setActiveModalId((prevId) => (prevId === id ? null : id)); // Toggle modal on/off
  };

  // Close modal on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setActiveModalId(null); // Close modal
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  const fetchPdf = async (id) => {
    try {
      const file = await exportAsPdf(id);

      if (!(file instanceof Blob)) {
        console.error("Error: Received invalid PDF file", file);
        return;
      }
      // console.log("PDF Blob:", file);
      const pdfUrl = window.URL.createObjectURL(file);

      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = "data.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Failed to fetch PDF:", error);
    }
  };

  const downloadCsv = async (id) => {
    try {
      const file = await exportAsCsv(id);

      if (!(file instanceof Blob)) {
        console.error("Error: Received invalid CSV file", file);
        return;
      }
      const url = window.URL.createObjectURL(file);

      const a = document.createElement("a");
      a.href = url;
      a.download = "data.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error("Failed to fetch CSV:", error);
    }
  };

  return (
    <>
      <div className="w-full">
        <h2 className="font-semibold  text-neutral1000 mt-4 mb-2">
          Recent Scans
        </h2>
        <p className="text-neutral600 text-sm">See top 5 scans.</p>

        {isLoading && !isSuccess && (
          <div className="h-[55vh] w-full flex items-center justify-center">
            <Spinner />
          </div>
        )}

        {isError && !isLoading && (
          <div className="h-[45vh] w-full flex justify-center flex-col items-center">
            <p className="font-semibold text-xl text-center">
              There was an error getting the data
            </p>
            <p className="text-center">{data?.message ?? ""}</p>
          </div>
        )}

        {!isLoading && isSuccess && data?.data?.data?.length > 0 && (
          <div className="mt-3 bg-white h-[100%]">
            <div className="overflow-x-auto">
              <div className="w-full border min-w-[1100px] border-neutral300 rounded-lg text-sm">
                <div className="flex justify-between text-left px-3 xl:px-6 py-2 bg-neutral3 rounded-t-lg border-b text-neutral700 font-medium w-full">
                  <span className="flex-1">Asset Name</span>
                  <span className="flex-1">Target</span>
                  <span className="flex-1 text-center">Status</span>
                  <span className="flex-1">Created At</span>
                  <span className="w-[12%] text-center">Action</span>
                </div>

                {data?.data?.data?.slice(0, 5).map((item, index) => {
                  // const isDeleteModalOpen = openDeleteModal === item.id;
                  const parts = item?.target?.target
                    .split(",")
                    .map((part) => part.trim());
                  return (
                    <section
                      key={index}
                      className={`min-h-[60px] py-2 px-3 xl:px-6 flex text-neutral900 items-center ${
                        index === 0
                          ? "border-t-0"
                          : "border-t border-t-[#EAECF0]"
                      } hover:bg-[#F9FAFB]`}
                    >
                      <div className="flex-1 text-left">{item.target.name}</div>
                      <div className="flex-1 relative flex-row flex gap-2 text-left break-all mr-2">
                        <div
                          key={index}
                          className={cn(
                            parts.length > 1 && "max-w-[200px]",
                            "w-fit px-2 py-1 rounded-full bg-neutral200"
                          )}
                        >
                          <p className="truncate">{parts[0]}</p>
                        </div>
                        {parts.length > 1 && (
                          <div
                            className="w-fit px-[10px] py-1 rounded-full bg-neutral200 cursor-pointer"
                            onMouseEnter={() => setShowExtra(index)}
                            onMouseLeave={() => setShowExtra(null)}
                          >
                            +{parts.length - 1}
                            {showExtra === index && (
                              <div className="absolute left-0 ml-0 bg-white border rounded shadow px-2 py-1 z-10">
                                {parts.slice(1, 4).join(", ")}
                                {parts.length > 4 && " ..."}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {/* <div className="flex-1 text-left">{item.status}</div> */}
                      <div className="flex-1 text-center text-xs">
                        {item.status === "DONE" ? (
                          <p className="flex items-center gap-2 bg-success10 text-success100 border-[1.5px] rounded-full border-[#00C8511A] w-fit px-2 py-1 mx-auto">
                            <FaCircleCheck className="text-[#00C851]" />
                            Completed
                          </p>
                        ) : (item.status === "PENDING" ||
                            item.status === "REQUESTED") &&
                          item.isScheduled === false ? (
                          <p className="flex items-center gap-2 border-cream100 text-brown100 border-[1.5px] rounded-full bg-[#FFBB331A] w-fit px-2 py-1 mx-auto">
                            <MdPending />
                            Pending
                          </p>
                        ) : item.isScheduled === true ? (
                          <p className="flex items-center gap-2 border-[#d3e8f1] text-[#0BA5EC] border-[1.5px] rounded-full bg-[#F1F8FF] w-fit px-2 py-1 mx-auto">
                            <MdPending />
                            Scheduled
                          </p>
                        ) : item.status === "RUNNING" ? (
                          <div className="flex justify-center">
                            <RunningStatus value={item?.progress || 0} />
                          </div>
                        ) : item.status === "STOPPED" ? (
                          <p className="flex items-center gap-2 bg-[#FF44441A] text-[#E12A5B] border-[1.5px] rounded-full border-[#FFC1C1] w-fit px-2 py-1 mx-auto">
                            <IoIosCloseCircle />
                            Stopped
                          </p>
                        ) : (
                          <p className="flex items-center gap-2 bg-[#FF44441A] text-[#E12A5B] border-[1.5px] rounded-full border-[#FFC1C1] w-fit px-2 py-1 mx-auto">
                            <IoIosCloseCircle />
                            Failed
                          </p>
                        )}
                      </div>
                      <div className="flex-1 text-left">
                        <span className="text-neutral800">
                          {formatFullDate(item.created_at)},
                        </span>{" "}
                        <span className="text-neutral600">
                          {formatTime(item.created_at)}
                        </span>
                      </div>

                      <div className="w-[12%] flex space-x-2 text-center justify-center">
                        {item?.status === "Done" ? (
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/dashboard/${item?.id}/report`}
                              className="hover:text-black"
                              state={{
                                report_id: item.report_id,
                                target_name: item.target.name,
                              }}
                            >
                              <span className="flex items-center gap-2 px-2 py-1 rounded-lg bg-neutral200">
                                <FiPieChart size={16} />
                                Report
                              </span>
                            </Link>

                            <span
                              className="px-2 py-[6px] rounded-lg hover:text-black bg-neutral200 cursor-pointer"
                              onClick={() => {
                                showModal(index);
                              }}
                            >
                              <LuDownload title="Export report" size={16} />
                            </span>
                          </div>
                        ) : (
                          <Link
                            className="hover:text-black"
                            to={`/dashboard/scan-history`}
                          >
                            <span className="flex justify-center items-center w-[124px] gap-2 px-2 py-1 rounded-lg bg-neutral200">
                              <FiCompass />
                              View Scan
                            </span>
                          </Link>
                        )}
                      </div>

                      {/* Modal */}
                      {activeModalId === index && ( // Show modal only for the active event
                        <div
                          className="w-fit bg-gray-200 absolute rounded-md top-12 right-16 z-10"
                          ref={modalRef}
                        >
                          <p
                            className="py-2 px-4 rounded-t-md hover:bg-gray-300 cursor-pointer"
                            onClick={() => {
                              fetchPdf(item?.report_id);
                              setActiveModalId(null); // Close modal
                            }}
                          >
                            Download as PDF
                          </p>
                          <p
                            className="py-2 px-4 rounded-b-md hover:bg-gray-300 cursor-pointer"
                            onClick={() => {
                              downloadCsv(item?.report_id);
                              setActiveModalId(null); // Close modal
                            }}
                          >
                            Download as CSV
                          </p>
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </div>
            {/* <PaginationComponent
            lastPage={data?.databases?.last_page!}
            currentPage={currentPage}
            handlePageChange={updatePage}
          /> */}
          </div>
        )}

        {data?.data?.data?.length === 0 && !isLoading && (
          // <Card className=" h-[40vh] mt-2 w-full flex justify-center flex-col items-center text-center">
          //   <Link to="/dashboard/scan-history">
          //     <Button className=" font-semibold text-sm">Run a scan</Button>
          //   </Link>
          // </Card>
          <Card className=" h-[40vh] w-full flex justify-center flex-col items-center text-center mt-3 p-4">
            <img src={empty} alt="empty" />
            <p className=" font-semibold my-2 text-darkGrey">
              No scans have been run
            </p>
            <p className="text-neutral600 text-sm max-w-[360px] mb-3">
              Initiate scans on your configured assets to understand the state
              of your web properties.
            </p>
            <Link to="/dashboard/scan-history">
              <Button
                variant={"outline"}
                className=" font-medium text-sm text-neutral900 pr-3"
              >
                <FiCompass className="text-[20px]" /> Run a scan
              </Button>
            </Link>
          </Card>
        )}
      </div>
    </>
  );
};

export default RecentScans;
